// assets/product-addons.js
// Complementos (chocolates, globos, etc.) en la ficha de producto.
// Si el cliente marca alguno, al "Agregar al carrito" se suman el arreglo y los
// complementos en un solo paso, todos con la misma fecha y horario de entrega.

class ProductAddons extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-form-id]');
    this.list = this.querySelector('[data-list]');
    this.error = this.querySelector('[data-error]');

    this.addEventListener('click', this.#onClick);
    this.list?.addEventListener('scroll', this.#updateArrows, { passive: true });
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    // Se puede tocar toda la tarjeta, no solo el botón +.
    const toggle = target.closest('.product-addons__card')?.querySelector('.product-addons__toggle');
    if (toggle) {
      const pressed = toggle.getAttribute('aria-pressed') !== 'true';
      toggle.setAttribute('aria-pressed', String(pressed));
      toggle.setAttribute('aria-label', `${pressed ? 'Quitar' : 'Agregar'} ${toggle.dataset.title || ''}`.trim());
      this.#setError('');
      return;
    }
    const arrow = target.closest('[data-scroll]');
    if (arrow && this.list) {
      const direction = Number(/** @type {HTMLElement} */ (arrow).dataset.scroll);
      this.list.scrollBy({ left: direction * this.list.clientWidth * 0.8, behavior: 'smooth' });
    }
  };

  #updateArrows = () => {
    if (!this.list) return;
    const [prev, next] = this.querySelectorAll('[data-scroll]');
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    if (prev) prev.disabled = this.list.scrollLeft <= 2;
    if (next) next.disabled = this.list.scrollLeft >= maxScroll - 2;
    const nav = this.querySelector('.product-addons__nav');
    if (nav) nav.hidden = maxScroll <= 2;
  };

  /** @returns {HTMLElement[]} */
  #selected() {
    return [...this.querySelectorAll('.product-addons__toggle[aria-pressed="true"]')];
  }

  /** ¿El cliente marcó algún complemento? (lo usa el calendario) */
  hasSelection() {
    return this.#selected().length > 0;
  }

  /**
   * Agrega el arreglo y los complementos marcados en un solo paso, todos con la
   * misma fecha y horario, y lleva al carrito. Lo llama el calendario después de validar.
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement | null} submitter
   */
  async addWithProduct(form, submitter) {
    const formData = new FormData(form);
    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string' && value) properties[match[1]] = value;
    }

    const parentTitle = this.root?.dataset.parentTitle || '';
    const items = [
      {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity')) || 1,
        properties,
      },
      ...this.#selected().map((toggle) => ({
        id: Number(toggle.dataset.variantId),
        quantity: 1,
        properties: { ...properties, 'Complemento de': parentTitle },
      })),
    ];

    submitter?.setAttribute('aria-busy', 'true');
    if (submitter) submitter.disabled = true;
    try {
      const routes = /** @type {any} */ (window).Shopify?.routes?.root || '/';
      const response = await fetch(`${routes}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'No se pudo agregar al carrito.');
      }
      window.location.href = `${routes}cart`;
    } catch (error) {
      submitter?.removeAttribute('aria-busy');
      if (submitter) submitter.disabled = false;
      this.#setError(error instanceof Error ? error.message : String(error));
    }
  }

  /** @param {string} message */
  #setError(message) {
    if (!this.error) return;
    this.error.hidden = !message;
    this.error.textContent = message;
  }
}

if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}
